import { DeleteOutlined, Error } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Grid2,
  IconButton,
} from "@mui/material";
import grey from "@mui/material/colors/grey";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import {
  BasicItem,
  Configuration,
  ExtendedFieldError,
  Project,
} from "product-test-types";

import { toDropdownItems } from "../../utils/dropdown-options";
import { getDateInputFormat } from "../../utils/format-date";
import {
  ControlledDateFormField,
  ControlledRichTextField,
  ControlledSelectField,
  ControlledTextFormField,
  FormFieldWrapper,
} from "../pimo-form";

type BasicItemRowProps = {
  index: number;
  field: BasicItem;
  setSelectedItemIndices: React.Dispatch<React.SetStateAction<string[]>>;
  currentSelectedItem: string[] | undefined;
  setCurrentSelectedItem: React.Dispatch<
    React.SetStateAction<string[] | undefined>
  >;
  selectedItemIndices: string[];
  formState: {
    basicItems: BasicItem[];
    configuration?: Configuration;
    projects?: Project[];
    validationErrors: Record<string, ExtendedFieldError>;
  };
  onFieldChange: (id: string, fieldKey: string, value: unknown) => void;
  setValue: (key: string, value: unknown) => void;
  itemName: {
    single: string;
    plural: string;
  };
  projectName: {
    single: string;
    plural: string;
  };
  handleDelete: (indices: string[]) => void;
  language?: string;
  showMaturity: boolean;
  showDimension: boolean;
};

export const BasicItemRow: PimoReactComponent<BasicItemRowProps> = ({
  field,
  setSelectedItemIndices,
  selectedItemIndices,
  formState,
  projectName,
  handleDelete,
  onFieldChange,
  language = "en",
  showMaturity,
  showDimension,
}) => {
  const getFieldDefinition = (fieldName: string) => {
    return formState.configuration?.fieldsDefinition
      ?.filter((field) => field.purpose === "itemBasic")
      ?.find((f) => f.fieldName === fieldName && f.enabled === true);
  };

  const getFieldIndexUsingId = (fieldId: string) => {
    return formState.basicItems.findIndex((item) => item.id === fieldId);
  };

  return (
    <Accordion
      sx={{
        boxShadow: "none",
        "&:last-of-type": {
          borderRadius: "12px",
        },
      }}
      key={field.id}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1-content"
        id="panel1-header"
        key={field.id}
      >
        <Grid2
          container
          alignItems="center"
          sx={{ width: "100%" }}
          key={field.id}
        >
          <Grid2 size={0.5}>
            <Checkbox
              onClick={(e) => {
                e.stopPropagation();
              }}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setSelectedItemIndices((prev) =>
                  isChecked
                    ? [...prev, field.id]
                    : prev.filter((i) => i !== field.id)
                );
              }}
              icon={
                <span
                  style={{
                    width: 20,
                    height: 20,
                    border: "2px solid",
                    borderColor: grey[700],

                    display: "inline-block",
                    backgroundColor: "#fff",
                  }}
                />
              }
              checkedIcon={
                <span
                  style={{
                    width: 20,
                    height: 20,

                    border: "2px solid",
                    borderColor: grey[700],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#fff",
                  }}
                >
                  <CheckIcon
                    style={{
                      fontSize: 18,
                      color: grey[700],
                    }}
                  />
                </span>
              }
              checked={selectedItemIndices.includes(field.id)}
            />
          </Grid2>
          <Grid2
            sx={{ fontSize: "14px" }}
            size={9.5 - (showMaturity ? 2 : 0) - (showDimension ? 2 : 0)}
          >
            {field.name}
          </Grid2>

          {showMaturity && (
            <Grid2 sx={{ fontSize: "14px" }} size={2}>
              {field.maturity || "-"}
            </Grid2>
          )}

          {showDimension && (
            <Grid2 sx={{ fontSize: "14px" }} size={2}>
              {field.dimension || "-"}
            </Grid2>
          )}

          <Grid2
            sx={{
              fontSize: "14px",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
            }}
            size={2}
          >
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleDelete([field.id])}
            >
              <DeleteOutlined />
            </IconButton>
            {Object.keys(formState?.validationErrors ?? {})?.find((item) =>
              item.startsWith(`basicItems.${getFieldIndexUsingId(field.id)}`)
            ) && <Error />}
          </Grid2>
        </Grid2>
      </AccordionSummary>
      <AccordionDetails key={field.id}>
        <Box
          sx={{
            flexGrow: 1,
            border: "1px solid",
            borderColor: grey[300],
            p: 2,
            borderRadius: "12px",
          }}
        >
          <Grid2 container alignItems="center" spacing={2}>
            {getFieldDefinition("name") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`${
                    getLocalizedMessage(language, "name") || "Name"
                  }${getFieldDefinition("name")?.isMandatory ? "*" : ""}`}
                  key={`name-${field.id}`}
                >
                  <ControlledTextFormField
                    value={field.name ?? ""}
                    key={`namefield-${field.id}`}
                    onChange={(e) => {
                      onFieldChange(field.id, "name", e);
                    }}
                    error={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.name`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.name`
                      ]?.message
                    }
                  />
                </FormFieldWrapper>
              </Grid2>
            )}
            {formState.configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemBasic")
              ?.find(
                (f) => f.fieldName === "customId" && f.enabled === true
              ) && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`${
                    getLocalizedMessage(language, "customId") || "Custom ID"
                  }${getFieldDefinition("customId")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledTextFormField
                    value={field.customId ?? ""}
                    onChange={(e) => {
                      onFieldChange(field.id, "customId", e);
                    }}
                    error={
                      formState?.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.customId`
                      ] !== undefined
                    }
                    helperText={
                      formState?.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.customId`
                      ]?.message
                    }
                  />
                </FormFieldWrapper>
              </Grid2>
            )}

            <Grid2 size={6}>
              <FormFieldWrapper
                label={`${projectName.single}*`}
                uiOptions={{ narrowInfoSlotIcon: true }}
                fieldWrapperSx={{
                  width: "100%",
                }}
              >
                <ControlledSelectField
                  value={
                    field.flag === "create"
                      ? "allProjects"
                      : Array.isArray(field.projectIds) &&
                          field.projectIds.length > 1
                        ? "allProjects"
                        : (field.projectIds?.[0]?.toString() ?? "")
                  }
                  options={
                    [{ name: `All ${projectName.plural}`, id: "allProjects" }]
                      .concat(
                        formState.projects
                          ?.filter((project) => project.name)
                          .map((project) => ({
                            name: project.name ?? "",
                            id: project.id ?? "",
                          })) ?? []
                      )
                      .map((project) => ({
                        label: project.name,
                        value: project.id,
                      })) ?? []
                  }
                  onChange={(value) => {
                    onFieldChange(field.id, "projectIds", value);
                  }}
                />
              </FormFieldWrapper>
            </Grid2>

            {getFieldDefinition("description") && (
              <Grid2 size={12}>
                <FormFieldWrapper
                  label={`${
                    getLocalizedMessage(language, "description") ||
                    "Description"
                  }${getFieldDefinition("description")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledRichTextField
                    value={field.description ?? ""}
                    onChange={(e) => {
                      onFieldChange(field.id, "description", e);
                    }}
                    error={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.description`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.description`
                      ]?.message
                    }
                  />
                </FormFieldWrapper>
              </Grid2>
            )}
            {getFieldDefinition("owner") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`Owner${getFieldDefinition("owner")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledTextFormField
                    value={field.owner ?? ""}
                    onChange={(e) => {
                      onFieldChange(field.id, "owner", e);
                    }}
                    error={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.owner`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.owner`
                      ]?.message
                    }
                  />
                </FormFieldWrapper>
              </Grid2>
            )}
            {getFieldDefinition("budget") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`Budget${getFieldDefinition("budget")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledTextFormField
                    value={field.budget?.toString() ?? ""}
                    onChange={(e) => {
                      onFieldChange(field.id, "budget", e);
                    }}
                    isNumber={true}
                    error={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.budget`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.budget`
                      ]?.message
                    }
                  />
                </FormFieldWrapper>
              </Grid2>
            )}
            {getFieldDefinition("startDate") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`${getLocalizedMessage(language, "startDate")}${getFieldDefinition("startDate")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledDateFormField
                    inputFormat={getDateInputFormat(language)}
                    value={field.startDate}
                    onChange={(value) => {
                      onFieldChange(field.id, "startDate", value);
                    }}
                    error={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.startDate`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.startDate`
                      ]?.message
                    }
                  />
                </FormFieldWrapper>
              </Grid2>
            )}
            {getFieldDefinition("endDate") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`${getLocalizedMessage(language, "endDate")}${getFieldDefinition("endDate")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledDateFormField
                    inputFormat={getDateInputFormat(language)}
                    value={field.endDate}
                    minDate={field.startDate}
                    onChange={(val) => {
                      onFieldChange(field.id, "endDate", val);
                    }}
                    error={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.endDate`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.endDate`
                      ]?.message
                    }
                  />
                </FormFieldWrapper>
              </Grid2>
            )}
            {getFieldDefinition("dropdownFieldOne") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`${
                    getFieldDefinition("dropdownFieldOne")?.label
                  }${getFieldDefinition("dropdownFieldOne")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledSelectField
                    options={toDropdownItems(
                      formState.configuration?.dropdownFieldOneDefinition
                    )}
                    value={field.dropdownFieldOne ?? ""}
                    onChange={(val) => {
                      onFieldChange(field.id, "dropdownFieldOne", val);
                    }}
                    error={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.dropdownFieldOne`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.dropdownFieldOne`
                      ]?.message
                    }
                  ></ControlledSelectField>
                </FormFieldWrapper>
              </Grid2>
            )}
            {showMaturity && getFieldDefinition("maturity") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`${getFieldDefinition("maturity")?.label}${getFieldDefinition("maturity")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledSelectField
                    options={
                      formState.configuration?.maturityConfiguration?.map(
                        (entry) => ({
                          label: entry.name,
                          value: entry.name,
                        })
                      ) ?? []
                    }
                    value={field.maturity ?? ""}
                    onChange={(val) => {
                      onFieldChange(field.id, "maturity", val);
                    }}
                    error={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.maturity`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.maturity`
                      ]?.message
                    }
                  ></ControlledSelectField>
                </FormFieldWrapper>
              </Grid2>
            )}
            {showDimension && getFieldDefinition("dimension") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`${getFieldDefinition("dimension")?.label}${getFieldDefinition("dimension")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledSelectField
                    options={
                      formState.configuration?.dimensionDefinition?.map(
                        (entry) => ({
                          label: entry.name,
                          value: entry.name,
                        })
                      ) ?? []
                    }
                    value={field.dimension ?? ""}
                    onChange={(val) => {
                      onFieldChange(field.id, "dimension", val);
                    }}
                    error={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.dimension`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `basicItems.${getFieldIndexUsingId(field.id)}.dimension`
                      ]?.message
                    }
                  ></ControlledSelectField>
                </FormFieldWrapper>
              </Grid2>
            )}
          </Grid2>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
